import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Spinner, Heading, Text, VStack } from '@chakra-ui/react';

import ShipStationCard from './components/ShipStationCard';
import { useColors } from './components/colors';

import {
  getThirdPartyIntegrationsWithPaginationApi,
  getThirdPartyIntegrationApi,
} from 'features/third-party-integrations/third-party-integrations.api';
import { getStoresListApi } from 'features/stores/stores.api';

const getThirdPartyData = (integration) => {
  const configs = integration.configurations || [];

  const clientIdConfig = configs.find((c) => c.key === 'clientId');
  const clientSecretConfig = configs.find((c) => c.key === 'clientSecret');
  const mappedStoresConfig = configs.find((c) => c.key === 'mappedStores');

  const apiId = clientIdConfig?.value || '';
  const apiSecret = clientSecretConfig?.value || '';
  const mappedStores = mappedStoresConfig?.value || [];

  return {
    id: integration._id,
    name: integration.name,
    altName: integration.altName,
    apiId,
    apiSecret,
    storesConnected: integration.shipStationStores?.length || 0,
    shipStationStores: integration.shipStationStores || [],
    mappedStores,
  };
};

const ThirdPartyIntegrations = () => {
  const dispatch = useDispatch();
  const { textColor } = useColors();
  const {
    thirdPartyIntegrations,
    thirdPartyIntegration,
    getThirdPartyIntegrationsStatus,
    getThirdPartyIntegrationStatus,
  } = useSelector((state) => state.thirdPartyIntegrations);

  const { list: storeOptions, getStoresListStatus } = useSelector(
    (state) => state.stores,
  );

  const loading =
    getThirdPartyIntegrationsStatus === 'pending' ||
    getThirdPartyIntegrationStatus === 'pending';

  useEffect(() => {
    dispatch(getStoresListApi());
    dispatch(getThirdPartyIntegrationsWithPaginationApi({ page: 1, limit: 10 }));
  }, [dispatch]);

  useEffect(() => {
    if (thirdPartyIntegrations.length > 0) {
      const shipStationIntegration = thirdPartyIntegrations.find(
        (integration) => integration.name === 'ship-station',
      );

      if (shipStationIntegration && shipStationIntegration._id) {
        dispatch(getThirdPartyIntegrationApi(shipStationIntegration._id));
      }
    }
  }, [thirdPartyIntegrations, dispatch]);

  if (loading) {
    return (
      <Box
        px={{ base: 4, md: 10 }}
        py={{ base: 8, md: 10 }}
        mt="50px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  return (
    <Box px={{ base: 4, md: 10 }} py={{ base: 8, md: 10 }} mt="50px" width="100%">
      <VStack align="stretch" spacing={8} mb={8}>
        <Box>
          <Heading size="lg" color={textColor} mb={2}>
            Third party integrations
          </Heading>
          <Text fontSize="md" color="gray.500" maxW="2xl">
            Connect external services, map stores, and automate order workflows.
          </Text>
        </Box>
      </VStack>

      {thirdPartyIntegration && thirdPartyIntegration.name === 'ship-station' && (
        <ShipStationCard
          key={thirdPartyIntegration._id}
          {...getThirdPartyData(thirdPartyIntegration)}
          storeOptions={storeOptions}
          getStoresListStatus={getStoresListStatus}
          integrationData={thirdPartyIntegration}
        />
      )}
    </Box>
  );
};

export default ThirdPartyIntegrations;
